import { CircularProgress, Divider } from '@mui/material';
import moment, { Moment, MomentInput } from 'moment';
import React, { useEffect, useState } from 'react';
import { checkValidInputEmail } from 'utils/functions';
import MarketplaceComponents from '../../MarketPlace';
import DatePicker from '../../commonComponents/DatePicker';
import Textbox from '../../commonComponents/Textbox';
import Typography from '../../commonComponents/Typography';
import OnBoardingComponent from '../../onBoardingComponent';
import {
  DatePickerWrapper,
  StyledActions,
  StyledDrawerContent,
  StyledForm,
} from './OffBoardingForm.style';
import { OffBoardingPayload, OffBoardingTaskDefinition } from './OffBoardingForm.type';

type PayloadKey = keyof OffBoardingPayload;
type Errors = { [key in PayloadKey]?: boolean };
type UserInfo = any;

export interface OffBoardingFormProps {
  initData?: OffBoardingPayload;
  open: boolean;
  onClose: () => void;
  onSubmit: (id: string, data: OffBoardingPayload) => void;
  getUserProfile?: (email: string) => UserInfo;
  titleForm?: string;
  taskDefinitions?: OffBoardingTaskDefinition[];
  isCancelOnboarding?: boolean;
  isOpenSubmenu?: boolean;
  locale?: Record<string, any>;
}

export const formatDate = (date: MomentInput = new Date()) => {
  return moment(date).endOf('day').toISOString();
};

const isDateBefore = (firstDate: MomentInput, secondDate: MomentInput) => {
  return moment(firstDate).isBefore(moment(secondDate), 'days');
};

const fifteenDayAfter = moment(new Date()).add(15, 'days');

const defaultEmployee: OffBoardingPayload = {
  personal_email: '',
  email: '',
  manager_email: '',
  last_working_date: formatDate(fifteenDayAfter),
  last_access_date: formatDate(fifteenDayAfter),
  tasks: [],
};

const OffBoardingForm = (props: OffBoardingFormProps) => {
  const {
    initData,
    open,
    onClose,
    onSubmit,
    getUserProfile,
    titleForm,
    taskDefinitions = [],
    isCancelOnboarding = false,
    isOpenSubmenu = false,
    locale,
  } = props;

  const isEditMode = !!initData?.id;

  const [employeeData, setEmployeeData] = useState<OffBoardingPayload>(defaultEmployee);
  const [validDateInput, setValidDateInput] = useState<boolean>(true);
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);
  const [loadingManager, setLoadingManager] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEmployeeData(initData || defaultEmployee);

    if (initData) {
      setErrors({
        last_access_date: isDateBefore(initData?.last_access_date, initData?.last_working_date),
      });
    } else {
      setErrors({});
    }
  }, [initData, open]);

  const handleClose = () => {
    onClose();
    setValidDateInput(true);
    setEmployeeData(defaultEmployee);
  };

  const handleChange = (key: PayloadKey, value: OffBoardingPayload[PayloadKey]) => {
    let newEmployeeData = { ...employeeData };
    setEmployeeData((prevState) => {
      newEmployeeData = { ...prevState, [key]: value };
      return newEmployeeData;
    });

    const isDateField = ['last_working_date', 'last_access_date'].includes(key);
    if (!isDateField) {
      setErrors((prevState) => ({ ...prevState, [key]: false }));
      return;
    } else {
      const isValid = moment(value, moment.ISO_8601, true).isValid();
      setValidDateInput(isValid);
    }

    const { last_working_date, last_access_date } = newEmployeeData;
    setErrors((prevState) => ({
      ...prevState,
      last_access_date: isDateBefore(last_access_date, last_working_date),
    }));
  };

  const handleEmailBlur = async () => {
    try {
      setLoadingManager(true);
      if (!checkValidInputEmail(employeeData.email) || !getUserProfile) return;
      const user = await getUserProfile(employeeData.email);
      if (!user.uid) return;
      handleChange('manager_email', user.manager_email);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingManager(false);
    }
  };

  const validateData = () => {
    const newErrors = { ...errors };
    const requiredFields: PayloadKey[] = ['email'];
    for (const key of requiredFields) {
      newErrors[key] = !employeeData[key];
    }
    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const isValid = validateData();
      if (!isValid) return;

      const { manager_email, ...payload } = employeeData;
      const tasks = taskDefinitions.map((task: any) => task.id);
      await onSubmit(initData?.id || '', { ...payload, tasks });
      handleClose();
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MarketplaceComponents.Drawer open={open} onClose={handleClose} isOpenSubmenu={isOpenSubmenu}>
      <StyledDrawerContent>
        <StyledForm>
          <Typography kind="h6">{titleForm}</Typography>

          <Textbox
            name="personal_email"
            label={locale?.personalEmail}
            placeholder={locale?.personalEmail}
            boldLabel
            labelSize="small"
            error={!!errors.personal_email}
            value={employeeData.personal_email || ''}
            onChange={(e) => handleChange('personal_email', e.target.value)}
            fullWidth
            disabled={isEditMode}
          />
          <Textbox
            name="email"
            label={locale?.email}
            placeholder={locale?.email}
            boldLabel
            labelSize="small"
            error={!!errors.email}
            value={employeeData.email || ''}
            onChange={(e) => handleChange('email', e.target.value)}
            onBlur={handleEmailBlur}
            fullWidth
            required
            disabled={isEditMode}
          />
          <Textbox
            name="manager_email"
            label={locale?.managerEmail}
            placeholder={locale?.managerEmail}
            boldLabel
            labelSize="small"
            error={!!errors.manager_email}
            value={employeeData.manager_email || ''}
            onChange={(e) => handleChange('manager_email', e.target.value)}
            fullWidth
            disabled
            InputProps={{
              endAdornment: loadingManager && <CircularProgress size={20} color="primary" />,
            }}
          />
          <DatePickerWrapper>
            <DatePicker
              label={locale?.lastWorkingDate}
              boldLabel
              value={moment(employeeData.last_working_date)}
              onChange={(date) => handleChange('last_working_date', formatDate(date))}
              required
              error={!!errors.last_working_date}
              useInitValue={false}
              slotProps={{
                textField: {
                  onKeyDown: (event) => event.preventDefault(),
                },
              }}
              // disablePast
            />
          </DatePickerWrapper>
          <DatePickerWrapper>
            <DatePicker
              label={locale?.lastAccessDate}
              boldLabel
              value={moment(employeeData.last_access_date)}
              onChange={(date) => handleChange('last_access_date', formatDate(date))}
              required
              error={!!errors.last_access_date}
              useInitValue={false}
              shouldDisableDate={(date: Moment) =>
                isDateBefore(date, employeeData.last_working_date)
              }
              slotProps={{
                textField: {
                  onKeyDown: (event) => event.preventDefault(),
                },
              }}
            />
          </DatePickerWrapper>

          <Divider />

          {(!isEditMode || (isCancelOnboarding && taskDefinitions?.length > 0)) && (
            <OnBoardingComponent.TaskList tasks={taskDefinitions} title={locale?.trainingTasks} />
          )}
        </StyledForm>

        <Divider />

        <StyledActions>
          <MarketplaceComponents.Button fullWidth loadingStart={submitting} onClick={handleSubmit} disabled={!validDateInput || !!errors.last_access_date} id="btn-offboarding_form-submit">
            {locale?.btnSubmit}
          </MarketplaceComponents.Button>
        </StyledActions>
      </StyledDrawerContent>
    </MarketplaceComponents.Drawer>
  );
};

OffBoardingForm.defaultProps = {
  locale: {
    personalEmail: 'Personal Email',
    email: 'Email',
    managerEmail: 'Manager Email',
    lastWorkingDate: 'Last Working Date',
    lastAccessDate: 'Last Access Date',
    trainingTasks: 'Training Tasks',
    btnSubmit: 'Submit',
  },
};

export default OffBoardingForm;
